import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Globe, ChevronUp, Check } from 'lucide-react';

interface Language {
  code: string;
  name: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'cs', name: 'Čeština' }
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState(i18n.language);

  // Update selected language when i18n.language changes
  React.useEffect(() => {
    setSelectedLanguage(i18n.language);
  }, [i18n.language]);

  const currentLanguage = languages.find(lang => lang.code === selectedLanguage) || languages[0];

  const changeLanguage = (languageCode: string) => {
    try {
      // Change i18next language
      i18n.changeLanguage(languageCode);
      setSelectedLanguage(languageCode);
      
      // Store the language preference
      localStorage.setItem('i18nextLng', languageCode);
      
      setIsOpen(false);
      
      // Construct the new path based on the current path and new language
      const currentPath = location.pathname;
      const pathSegments = currentPath.split('/').filter(Boolean);

      const supportedLangs = ['en', 'es', 'de', 'it', 'cs'];
      let newPath;

      if (pathSegments.length > 0 && supportedLangs.includes(pathSegments[0])) {
        // Replace the existing language prefix
        pathSegments[0] = languageCode;
        newPath = `/${pathSegments.join('/')}`;
      } else {
        // Prepend the language code if no language prefix exists
        newPath = `/${languageCode}${currentPath.startsWith('/') ? currentPath : `/${currentPath}`}`;
      }

      // Use navigate to trigger a proper React Router navigation
      navigate(newPath);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };
  
  // Update document language on mount and language change
  React.useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 bg-primary-950 border border-primary-800 text-gray-400 hover:text-white transition-colors py-2 focus:outline-none focus:ring-2 focus:ring-white/20 rounded-lg px-4"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls="language-menu"
        aria-label={`Select language. Current language: ${currentLanguage.name}`}
      >
        <Globe size={16} className="opacity-70" aria-hidden="true" />
        <span className="text-sm">{currentLanguage.name}</span>
        <ChevronUp 
          size={16} 
          className={`transition-transform duration-200 ${isOpen ? '' : 'rotate-180'}`} 
          aria-hidden="true" 
        />
      </button>

      {isOpen && (
        <div 
          id="language-menu"
          className="absolute left-0 bottom-full mb-2 py-2 w-48 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="language-menu"
        >
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-200 focus:outline-none focus:bg-gray-200 flex items-center justify-between
                ${language.code === selectedLanguage ? 'text-primary font-medium bg-gray-50' : 'text-gray-700'}`}
              role="menuitem"
              aria-selected={language.code === selectedLanguage}
              aria-label={`${language.name}${language.code === selectedLanguage ? ', currently selected' : ''}`}
            >
              <div>{language.name}</div>
              {language.code === selectedLanguage && (
                <Check 
                  size={16} 
                  className="text-primary" 
                  aria-hidden="true"
                  role="presentation" 
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;